import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, ShieldX } from 'lucide-react';
import { Empty } from '@shining-technologies/ui';
import { useAuth } from '@/features/auth/AuthProvider';
import { loginPathFor } from '@/features/auth/session';
import { usePermission } from '@/hooks/usePermission';
import type { Action } from '@/lib/permissions';

/** Gate a route on authentication and (optionally) a permission. */
export function ProtectedRoute({ action, children }: { action?: Action; children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const { can } = usePermission();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  if (!user) {
    // FIX(admin-protected-route-returnto): Before: `returnTo` was built raw from
    // the location (and dropped the hash). After: routed through the same
    // `safeReturnTo` LoginPage and the 401 handler use (via `loginPathFor`), so
    // an unsafe or `/login` target collapses to plain `/login`.
    return <Navigate to={loginPathFor(location.pathname + location.search + location.hash)} replace />;
  }

  if (action && !can(action)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Empty
          status="error"
          icon={<ShieldX aria-hidden />}
          title="Access denied"
          description="Your role doesn't have permission to view this page."
        />
      </div>
    );
  }

  return <>{children}</>;
}
